// ═══════════════════════════════════════════════════════════════
// Paso 3: Previsualizar resultados de validación y confirmar
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import {
  CheckCircle2, AlertTriangle, XCircle, Users,
  AlertCircle, Loader2, ArrowUpRight
} from 'lucide-react';
import ValidationTable from '../components/ValidationTable';

const StatCard = ({ icon, value, label, color }) => (
  <div className={`rounded-xl border p-4 text-center ${color}`}>
    <div className="flex justify-center mb-1">{icon}</div>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-xs mt-0.5">{label}</p>
  </div>
);

const Step3_Preview = ({ resumen, filas, onConfirm, onBack, loading, error }) => {
  const [modo, setModo] = useState('solo_validos');

  const {
    total_filas = 0,
    total_validos = 0,
    total_warnings = 0,
    total_errores = 0,
    puede_importar = false,
  } = resumen ?? {};

  const importablesCount = modo === 'solo_validos'
    ? total_validos
    : total_validos + total_warnings;

  const canConfirm = puede_importar && importablesCount > 0;

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Resultado de la validación
        </h3>
        <p className="text-sm text-gray-500">
          Revisamos <strong>{total_filas}</strong> fila{total_filas !== 1 ? 's' : ''} en el archivo.
          Confirma qué deseas importar.
        </p>
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard
          icon={<Users className="w-5 h-5 text-gray-500" />}
          value={total_filas}
          label="Total filas"
          color="bg-gray-50 border-gray-200 text-gray-700"
        />
        <StatCard
          icon={<CheckCircle2 className="w-5 h-5 text-green-500" />}
          value={total_validos}
          label="Sin errores"
          color="bg-green-50 border-green-200 text-green-700"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5 text-yellow-500" />}
          value={total_warnings}
          label="Advertencias"
          color="bg-yellow-50 border-yellow-200 text-yellow-700"
        />
        <StatCard
          icon={<XCircle className="w-5 h-5 text-red-500" />}
          value={total_errores}
          label="Errores críticos"
          color="bg-red-50 border-red-200 text-red-700"
        />
      </div>

      {/* Bloqueo total */}
      {!puede_importar && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">No hay filas importables</p>
            <p className="text-xs text-red-600 mt-0.5">
              Todas las filas tienen errores críticos. Corrige el archivo y vuelve a subirlo.
            </p>
          </div>
        </div>
      )}

      {/* Selector de modo (solo si hay warnings) */}
      {puede_importar && total_warnings > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <p className="text-sm font-medium text-blue-900">¿Qué deseas importar?</p>
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="radio"
                name="modo"
                value="solo_validos"
                checked={modo === 'solo_validos'}
                onChange={() => setModo('solo_validos')}
                className="mt-0.5 accent-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-800">
                  Solo los válidos ({total_validos} empleado{total_validos !== 1 ? 's' : ''})
                </span>
                <p className="text-xs text-gray-500">
                  Se importan únicamente las filas sin ningún problema.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="radio"
                name="modo"
                value="incluir_warnings"
                checked={modo === 'incluir_warnings'}
                onChange={() => setModo('incluir_warnings')}
                className="mt-0.5 accent-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-800">
                  Válidos + advertencias ({total_validos + total_warnings} empleado{(total_validos + total_warnings) !== 1 ? 's' : ''})
                </span>
                <p className="text-xs text-gray-500">
                  Se importan los válidos y los que solo tienen advertencias menores (ej: email incompleto).
                  Los errores críticos siempre se excluyen.
                </p>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Info resumen de lo que se va a importar */}
      {canConfirm && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <ArrowUpRight className="w-4 h-4 flex-shrink-0" />
          Se importarán <strong>{importablesCount}</strong> empleado{importablesCount !== 1 ? 's' : ''}.
          {total_errores > 0 && (
            <span className="ml-1 text-green-700">
              ({total_errores} fila{total_errores !== 1 ? 's' : ''} con errores críticos se excluirá{total_errores !== 1 ? 'n' : ''}.)
            </span>
          )}
        </div>
      )}

      {/* Tabla de filas */}
      <ValidationTable filas={filas} />

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          onClick={onBack}
          disabled={loading}
          className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          ← Subir otro archivo
        </button>

        <button
          onClick={() => onConfirm(modo)}
          disabled={!canConfirm || loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Importando...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Confirmar e importar {canConfirm ? `(${importablesCount})` : ''}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Step3_Preview;
